// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Renders upload slots, optionally grouped by feature/track.
 * Config-driven: reads UploadSlot[] and optional feature groups.
 */
import { useState, type FC, type ComponentType } from 'react'
import { Plus } from 'lucide-react'
import { Button, TooltipProvider } from '@/components/ui'
import type { UploadSlot } from '../types'
import { SlotUploadCard, type SlotDocument } from './SlotUploadCard'
import { AddAdditionalDocumentDialog } from './AddAdditionalDocumentDialog'

/** Feature group for visual slot grouping (e.g., track → slots) */
export interface SlotFeatureGroup {
  key: string
  label: string
  icon?: ComponentType<{ className?: string }>
  slotIds: string[]
  /** Tailwind border class for left accent */
  borderClass?: string
  /** Tailwind color class for icon */
  iconClass?: string
  /** When true, render an "Add additional document" affordance below this group's slots */
  allowAdditional?: boolean
}

interface SlotUploadGridProps {
  slots: UploadSlot[]
  /** If provided, groups slots by feature. Slots not in any group are shown ungrouped. */
  featureGroups?: SlotFeatureGroup[]
  /** Map slot ID → document for that slot */
  documentsBySlot: Map<string, SlotDocument>
  onUpload: (file: File, slotId: string) => Promise<void>
  onDelete?: (documentId: string) => Promise<void>
  /** Called when the user adds an additional document via the dialog. Required if any group sets allowAdditional. */
  onAddAdditional?: (groupKey: string, label: string, file: File) => Promise<void> | void
  disabled?: boolean
  /** Card vs inline row style */
  variant?: 'inline' | 'card'
}

export const SlotUploadGrid: FC<SlotUploadGridProps> = ({
  slots,
  featureGroups,
  documentsBySlot,
  onUpload,
  onDelete,
  onAddAdditional,
  disabled = false,
  variant = 'inline',
}) => {
  const [addDialogGroupKey, setAddDialogGroupKey] = useState<string | null>(null)

  if (featureGroups && featureGroups.length > 0) {
    return (
      <TooltipProvider delayDuration={300}>
        <div className="space-y-4">
          {featureGroups.map((group) => {
            const groupSlots = slots.filter((s) => group.slotIds.includes(s.id))
            if (groupSlots.length === 0 && !group.allowAdditional) return null
            const Icon = group.icon

            return (
              <div
                key={group.key}
                className={`rounded-lg border bg-card ${group.borderClass ?? ''} overflow-hidden`}
              >
                <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b">
                  {Icon && <Icon className={`h-4 w-4 ${group.iconClass ?? ''}`} />}
                  <span className="text-sm font-medium text-foreground">{group.label}</span>
                </div>
                <div className={variant === 'card' ? 'p-3 space-y-3' : 'divide-y'}>
                  {groupSlots.map((slot) => (
                    <SlotUploadCard
                      key={slot.id}
                      slot={slot}
                      document={documentsBySlot.get(slot.id) ?? null}
                      onUpload={onUpload}
                      onDelete={onDelete}
                      disabled={disabled}
                      variant={variant}
                    />
                  ))}
                </div>
                {group.allowAdditional && onAddAdditional && (
                  <div className="px-4 py-2 border-t bg-muted/10">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAddDialogGroupKey(group.key)}
                      disabled={disabled}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add additional document
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {onAddAdditional && (
          <AddAdditionalDocumentDialog
            open={addDialogGroupKey !== null}
            onOpenChange={(open) => {
              if (!open) setAddDialogGroupKey(null)
            }}
            onSubmit={async (label, file) => {
              if (addDialogGroupKey) {
                await onAddAdditional(addDialogGroupKey, label, file)
              }
            }}
          />
        )}
      </TooltipProvider>
    )
  }

  // Flat list (no grouping)
  if (variant === 'card') {
    return (
      <div className="space-y-3">
        {slots.map((slot) => (
          <SlotUploadCard
            key={slot.id}
            slot={slot}
            document={documentsBySlot.get(slot.id) ?? null}
            onUpload={onUpload}
            onDelete={onDelete}
            disabled={disabled}
            variant="card"
          />
        ))}
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="border rounded-lg divide-y">
        {slots.map((slot) => (
          <SlotUploadCard
            key={slot.id}
            slot={slot}
            document={documentsBySlot.get(slot.id) ?? null}
            onUpload={onUpload}
            onDelete={onDelete}
            disabled={disabled}
            variant="inline"
          />
        ))}
      </div>
    </TooltipProvider>
  )
}
